import React, { useState, useContext } from "react";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaChevronDown, FaChevronUp, FaPlus, FaSave } from "react-icons/fa";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";

export default function EmployeeForm() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [step, setStep] = useState(1);
  const [showFilters, setShowFilters] = useState(true);

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 4));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const clearAllFilters = () => {
    toast.success("All filters cleared");
  };

  return (
    <PayRollLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE FORM</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white text-black`
        }`}>
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}>
                Employee Details
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark 
                  ? "bg-blue-900 text-blue-100" 
                  : "bg-blue-100 text-blue-800"
              }`}>
                Step {step} of 4
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Hide/Show Filters Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Steps" : "Show Steps"}
              </button>

              {/* Reset Form Button */}
              <button
                onClick={clearAllFilters}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-orange-700 hover:bg-orange-600 text-white" 
                    : "bg-orange-600 hover:bg-orange-700 text-white"
                }`}
              >
                Reset Form
              </button>

              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-green-700 hover:bg-green-600 text-white" 
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          {/* Step Tabs - Collapsible */}
          <div className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
            isDark 
              ? "border-gray-700" 
              : "border-gray-200"
          }`}>
            {/* Step Header - Always Visible */}
            <div 
              className={`p-4 cursor-pointer transition-colors duration-200 ${
                isDark 
                  ? "bg-gray-800 hover:bg-gray-750" 
                  : "bg-gray-50 hover:bg-gray-100"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  EMPLOYEE FORM STEPS
                </h3>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}>
                    Click to {showFilters ? 'hide' : 'show'} steps
                  </span>
                  {showFilters ? (
                    <FaChevronUp className="text-lg" />
                  ) : (
                    <FaChevronDown className="text-lg" />
                  )}
                </div>
              </div>
            </div>

            {/* Step Content - Collapsible */}
            <div className={`transition-all duration-300 ease-in-out ${
              showFilters 
                ? 'max-h-96 opacity-100' 
                : 'max-h-0 opacity-0 overflow-hidden'
            }`}>
              <div className={`p-6 border-t ${
                isDark 
                  ? "bg-gray-800 border-gray-700" 
                  : "bg-gray-50 border-gray-200"
              }`}>
                <div className="flex justify-around pb-2 mb-6 font-semibold text-gray-700">
                  <button
                    onClick={() => setStep(1)}
                    className={`px-4 py-2 rounded-lg ${
                      step === 1 
                        ? `bg-${baseColor}-${baseShade} text-white` 
                        : isDark 
                          ? "bg-gray-700 text-gray-300" 
                          : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Basic Details
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    className={`px-4 py-2 rounded-lg ${
                      step === 2 
                        ? `bg-${baseColor}-${baseShade} text-white` 
                        : isDark 
                          ? "bg-gray-700 text-gray-300" 
                          : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Contact Information
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    className={`px-4 py-2 rounded-lg ${
                      step === 3 
                        ? `bg-${baseColor}-${baseShade} text-white` 
                        : isDark 
                          ? "bg-gray-700 text-gray-300" 
                          : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Personal Details
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    className={`px-4 py-2 rounded-lg ${
                      step === 4 
                        ? `bg-${baseColor}-${baseShade} text-white` 
                        : isDark 
                          ? "bg-gray-700 text-gray-300" 
                          : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Status
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="rounded-xl border overflow-hidden mb-6">
            {/* Step 1 - Basic Details */}
            {step === 1 && (
              <div className={`p-6 ${isDark ? "bg-gray-900" : "bg-white"}`}>
                <h2 className={`text-lg font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Basic Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Department</label>
                    <input className={inputClass} placeholder="Department" />
                  </div>
                  <div>
                    <label className={labelClass}>Designation</label>
                    <input className={inputClass} placeholder="Designation" />
                  </div>
                  <div>
                    <label className={labelClass}>Employee Code</label>
                    <input className={inputClass} placeholder="Employee Code" />
                  </div>
                  <div>
                    <label className={labelClass}>Employee Name</label>
                    <input className={inputClass} placeholder="Employee Name" />
                  </div>
                  <div>
                    <label className={labelClass}>Last Name</label>
                    <input className={inputClass} placeholder="Last Name" />
                  </div>
                  <div>
                    <label className={labelClass}>Father Name</label>
                    <input className={inputClass} placeholder="Father Name" />
                  </div>
                  <div>
                    <label className={labelClass}>Sex (Male/Female)</label>
                    <input className={inputClass} placeholder="Sex (Male/Female)" />
                  </div>
                  <div>
                    <label className={labelClass}>Blood Group</label>
                    <input className={inputClass} placeholder="Blood Group" />
                  </div>
                  <div>
                    <label className={labelClass}>Date of Joining</label>
                    <input type="date" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Employee Type</label>
                    <input className={inputClass} placeholder="Employee Type" />
                  </div>
                  <div>
                    <label className={labelClass}>Bank Name</label>
                    <input className={inputClass} placeholder="Bank Name" />
                  </div>
                  <div>
                    <label className={labelClass}>A/C Number</label>
                    <input className={inputClass} placeholder="A/C Number" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 - Contact Information */}
            {step === 2 && (
              <div className={`p-6 ${isDark ? "bg-gray-900" : "bg-white"}`}>
                <h2 className={`text-lg font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Temporary Address</label>
                    <input className={inputClass} placeholder="Temporary Address" />
                  </div>
                  <div>
                    <label className={labelClass}>Permanent Address</label>
                    <input className={inputClass} placeholder="Permanent Address" />
                  </div>
                  <div>
                    <label className={labelClass}>Country</label>
                    <input className={inputClass} placeholder="Country" />
                  </div>
                  <div>
                    <label className={labelClass}>State</label>
                    <input className={inputClass} placeholder="State" />
                  </div>
                  <div>
                    <label className={labelClass}>District</label>
                    <input className={inputClass} placeholder="District" />
                  </div>
                  <div>
                    <label className={labelClass}>City</label>
                    <input className={inputClass} placeholder="City" />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input className={inputClass} placeholder="Phone" />
                  </div>
                  <div>
                    <label className={labelClass}>Mobile</label>
                    <input className={inputClass} placeholder="Mobile" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Email Id</label>
                    <input className={inputClass} placeholder="Email Id" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 - Personal Details */}
            {step === 3 && (
              <div className={`p-6 ${isDark ? "bg-gray-900" : "bg-white"}`}>
                <h2 className={`text-lg font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Personal Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Marital Status</label>
                    <input className={inputClass} placeholder="Marital Status" />
                  </div>
                  <div>
                    <label className={labelClass}>Date of Birth</label>
                    <input type="date" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Driving License Number</label>
                    <input className={inputClass} placeholder="Driving License Number" />
                  </div>
                  <div>
                    <label className={labelClass}>Driving License Expiry Date</label>
                    <input type="date" className={inputClass} />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Upload Document</label>
                    <input type="file" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Qualification</label>
                    <input className={inputClass} placeholder="Qualification" />
                  </div>
                  <div>
                    <label className={labelClass}>Year of Completion</label>
                    <input className={inputClass} placeholder="Year of Completion" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4 - Status */}
            {step === 4 && (
              <div className={`p-6 ${isDark ? "bg-gray-900" : "bg-white"}`}>
                <h2 className={`text-lg font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Status</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Gross Salary</label>
                    <input className={inputClass} placeholder="Gross Salary" />
                  </div>
                  <div>
                    <label className={labelClass}>Special HRA</label>
                    <input className={inputClass} placeholder="Special HRA" />
                  </div>
                  <div>
                    <label className={labelClass}>PF UAN No</label>
                    <input className={inputClass} placeholder="PF UAN No" />
                  </div>
                  <div>
                    <label className={labelClass}>PF No</label>
                    <input className={inputClass} placeholder="PF No" />
                  </div>
                  <div>
                    <label className={labelClass}>Branch</label>
                    <input className={inputClass} placeholder="Branch" />
                  </div>
                  <div>
                    <label className={labelClass}>Grade</label>
                    <input className={inputClass} placeholder="Grade" />
                  </div>
                  <div>
                    <label className={labelClass}>PAN Number</label>
                    <input className={inputClass} placeholder="PAN Number" />
                  </div>
                  <div>
                    <label className={labelClass}>ESI Number</label>
                    <input className={inputClass} placeholder="ESI Number" />
                  </div>
                  <div>
                    <label className={labelClass}>Shift</label>
                    <input className={inputClass} placeholder="Shift" />
                  </div>
                  <div>
                    <label className={labelClass}>Salary Basis</label>
                    <input className={inputClass} placeholder="Salary Basis" />
                  </div>
                  <div>
                    <label className={labelClass}>CLA Allowance</label>
                    <input className={inputClass} placeholder="CLA Allowance" />
                  </div>
                  <div>
                    <label className={labelClass}>Daily Bata</label>
                    <input className={inputClass} placeholder="Daily Bata" />
                  </div>
                  <div>
                    <label className={labelClass}>Leave Per Month</label>
                    <input className={inputClass} placeholder="Leave Per Month" />
                  </div>
                  <div>
                    <label className={labelClass}>Leave Eligible</label>
                    <input className={inputClass} placeholder="Leave Eligible" />
                  </div>
                  <div>
                    <label className={labelClass}>Income Tax</label>
                    <input className={inputClass} placeholder="Income Tax" />
                  </div>
                  <div>
                    <label className={labelClass}>Payment Mode</label>
                    <input className={inputClass} placeholder="Payment Mode" />
                  </div>
                  <div>
                    <label className={labelClass}>Salary Scheme</label>
                    <input className={inputClass} placeholder="Salary Scheme" />
                  </div>
                  <div>
                    <label className={labelClass}>Bonus Bata</label>
                    <input className={inputClass} placeholder="Bonus Bata" />
                  </div>
                  <div>
                    <label className={labelClass}>Voter Id</label>
                    <input className={inputClass} placeholder="Voter Id" />
                  </div>
                  <div>
                    <label className={labelClass}>Aadhar Card Number</label>
                    <input className={inputClass} placeholder="Aadhar Card Number" />
                  </div>
                  <div>
                    <label className={labelClass}>IFSC Code</label>
                    <input className={inputClass} placeholder="IFSC Code" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Remarks / Details</label>
                    <textarea className={inputClass} placeholder="Remarks / Details" rows="3"></textarea>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            {step > 1 && (
              <button
                onClick={prevStep}
                className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-gray-700 hover:bg-gray-600 text-white" 
                    : "bg-gray-300 hover:bg-gray-400 text-gray-700"
                }`}
              >
                Back
              </button>
            )}
            {step < 4 ? (
              <button
                onClick={nextStep}
                className={`ml-auto px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                Next
              </button>
            ) : (
              <button
                onClick={() => toast.success("Form Submitted!")}
                className={`ml-auto flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark 
                    ? "bg-green-700 hover:bg-green-600 text-white" 
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                <FaSave />
                Submit
              </button>
            )}
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </PayRollLayoutWrapper>
  );
} 